import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Modal from 'react-modal';
import { Button, Form, InputGroup, Row, Col, Table, Badge, Collapse, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPoundSign, faTimes, faCheckSquare, faSquare,
    faChevronDown, faChevronUp, faDoorOpen, faInfoCircle, faSearch
} from '@fortawesome/free-solid-svg-icons';
import { doc, getDoc, getDocs, setDoc, updateDoc, collection } from 'firebase/firestore';
import { db } from '../../../firebase.init';
import { format } from 'date-fns';
import { ref, getStorage, uploadBytes, getDownloadURL } from "firebase/storage";

const getSavedDiscountValue = (savedData) => {
    const previousValue = savedData?.discountType === 'custom'
        ? savedData?.customDiscount
        : savedData?.discountType;
    const rawValue = savedData?.discountValue ?? previousValue;

    if (rawValue === undefined || rawValue === null || rawValue === '' || rawValue === 'none') {
        return '';
    }

    const percentage = Number(rawValue);
    if (!Number.isFinite(percentage) || percentage <= 0) {
        return '';
    }

    return String(Math.min(100, percentage));
};

const EstimateModal = ({
    show,
    onHide,
    doorData,
    doorsData,
    buildingData,
    foundBuildingUid,
    customEstimateData,
    isResidential = false,
    onGenerate
}) => {

    const [activeTab, setActiveTab] = useState('details');
    const [loading, setLoading] = useState(false);
    const [isDoorSelectorOpen, setIsDoorSelectorOpen] = useState(false);
    const [showDoorSelector, setShowDoorSelector] = useState(false);
    const [doorSearchTerm, setDoorSearchTerm] = useState('');
    const [exportNewLayout, setExportNewLayout] = useState(false);

    const [prices, setPrices] = useState({});
    const [codes, setCodes] = useState({});
    const [quantities, setQuantities] = useState({});
    const [doorRefs, setDoorRefs] = useState({});
    const [selectedWorkItems, setSelectedWorkItems] = useState(new Set());
    const [workItemDescriptions, setWorkItemDescriptions] = useState({});
    const [loadedCustomWorkItems, setLoadedCustomWorkItems] = useState([]);
    const [customDescription, setCustomDescription] = useState('');
    const [customDoorRef, setCustomDoorRef] = useState('');
    const [isLoadingExistingEstimate, setIsLoadingExistingEstimate] = useState(false);

    const [laborCost, setLaborCost] = useState('0');
    const [discountValue, setDiscountValue] = useState('');
    const [discountType, setDiscountType] = useState('percentage');
    const [discountAmountInput, setDiscountAmountInput] = useState('');
    const [isEditingDiscount, setIsEditingDiscount] = useState(false);

    const [includeVAT, setIncludeVAT] = useState(false);
    const [includeManagementFee, setIncludeManagementFee] = useState(false);

    const [estimateNumber, setEstimateNumber] = useState('');
    const [selectedDoorIds, setSelectedDoorIds] = useState(new Set());
    const [clientDetails, setClientDetails] = useState({
        clientName: '',
        clientEmail: '',
        clientAccountEmail: '',
        clientTelephone: '',
    });
    const [payeeDetails, setPayeeDetails] = useState({
        payeeName: '',
        invoiceAddress: '',
        invoiceAddress2: '',
        invoiceAddress3: '',
        invoicePostcode: '',
    });
    const [propertyDetails, setPropertyDetails] = useState({
        propertyAddress: '',
        propertyAddress2: '',
        propertyAddress3: '',
        propertyPostcode: '',
    });

    const storage = getStorage();

    const prevShowRef = useRef(false);
    const hasInitializedRef = useRef(false);
    const prevBuildingUidRef = useRef(null);
    const savedQuantitiesRef = useRef(null);

    const GAP_MAP = {
        fD1: "Lock",
        fD2: "Hinge",
        fD3: "Top",
        fD4: "Bottom"
    };

    const GAP_THRESHOLDS = {
        bottom: { major: 12, minor: 9 },
        default: { major: 7, minor: 5 }
    };

    const pricingTable = {
        'replace non-compliant glass': { code: 'RNCG', price: 400 },
        'replace non-compliant glass': { code: 'RNCG', price: 400 },
        'replace missing screws from hinge': { code: 'RMS', price: 25 },
        'replace misssing screws from hinge': { code: 'RMS', price: 25 },
        'screws missing from hinge': { code: 'RMS', price: 25 },
        'replace missing screws from hinges': { code: 'MS', price: 25 },
        'replace non-compliant letter box': { code: 'RLS', price: 100 },
        'replace hinges with fire rated set (lock & hinge gap over 2mm)': { code: 'RH', price: 75 },
        'replace hinges with fire rated set (lock & hinge gap 2mm or lower': { code: 'RHS', price: 150 },
        'replace non-compliant hinge': { code: 'RH', price: 75 },
        'replace hinge': { code: 'RH', price: 75 },
        'replace non compliant fire door fd60 (leaf only)': { code: 'RFD60', price: 800 },
        'replace non compliant fire door fd30 (leaf only)': { code: 'RFD30', price: 650 },
        'replace damaged door': { code: 'RFD30', price: 650 },
        'replace complete door set fd60': { code: 'RDS60', price: 1500 },
        'replace complete door set fd30': { code: 'RDS30', price: 1200 },
        'replace complete door set fd30 (small)': { code: 'RDS30 (small)', price: 950 },
        'replace complete door set inc hinges, closer and strips': { code: 'COMP30', price: 1600 },
        'replace damaged intumescent strip': { code: 'RDIS', price: 75 },
        'install new intermittent strip': { code: 'INIS', price: 100 },
        'install intumescent strips': { code: 'INIS', price: 100 },
        'intumescent protection to hinges or locks': { code: 'IP', price: 65 },
        'repair damaged door leaf': { code: 'RDDL', price: 100 },
        'repair damaged door': { code: 'RDDL', price: 100 },
        'replace cold smoke seals': { code: 'RCSS', price: 65 },
        'fit cold smoke seals': { code: 'FCSS', price: 100 },
        'install new door closer': { code: 'INDC', price: 200 },
        'adjust door closer': { code: 'ADC', price: 30 },
        'repair door closer': { code: 'ADC', price: 30 },
        'install new drop down seal': { code: 'IDDS', price: 250 },
        'install compliant signage': { code: 'ICS', price: 22.50 },
        'install relevant signage': { code: 'ICS', price: 22.50 },
        'adjust minor door gap-top (less than 6mm)': { code: 'ADGT', price: 100 },
        'adjust major door gap-top (more than 6mm)': { code: 'ADGTm', price: 200 },
        'adjust gap - top': { code: 'ADGT', price: 100 },
        'adjust minor door gap-lock (less than 6mm)': { code: 'ADGL', price: 100 },
        'adjust major door gap-lock (more than 6mm)': { code: 'ADGLm', price: 200 },
        'adjust gap - lock': { code: 'ADGL', price: 100 },
        'adjust minor door gap-hinge (less than 6mm)': { code: 'ADGH', price: 100 },
        'adjust major door gap-hinge (more than 6mm)': { code: 'ADGHm', price: 200 },
        'adjust gap - hinge': { code: 'ADGH', price: 100 },
        'adjust bottom door gap': { code: 'ABD', price: 200 },
        'adjust gap - bottom': { code: 'ABD', price: 200 },
        'adjust all gaps (major)': { code: 'AAGm', price: 300 },
        'adjust all gaps': { code: 'AAG', price: 200 },
        'replace door latch': { code: 'RDL', price: 30 },
        'replace top door latch': { code: 'RDL', price: 30 },
        'reattach top door latch': { code: 'RDL', price: 30 },
        'replace door handle': { code: 'RDH', price: 50 },
        'repair door handle': { code: 'RPDH', price: 25 },
        'replace key-hole cover': { code: 'RKC', price: 10 },
        'tighten keyhole cover screws': { code: 'RKC', price: 10 },
        'repair door frame': { code: 'RDF', price: 80 },
        'replace door lock': { code: 'RDFL', price: 50 },
        'replace non-compliant locks': { code: 'RDFL', price: 50 },
        'tighten door lock': { code: 'TDL', price: 25 },
        'install third hinge': { code: 'ITH', price: 25 },
        'install third fire-rated hinge': { code: 'ITH', price: 25 },
        'install third fire rated hinge': { code: 'ITH', price: 25 },
        'replace spy hole': { code: 'RSH', price: 25 },
        'install spy hole': { code: 'ISH', price: 75 },
        'install new fb 2 locks': { code: 'INFB2', price: 115 },
        'install new fb 1 locks': { code: 'INFB2', price: 100 },
        'intrusive door inspection': { code: 'IDI', price: 150 },
        'install keyles exit': { code: 'IKE', price: 100 },
        'install keyless exit': { code: 'IKE', price: 100 },
    };

    const getIdsFromUrl = useCallback(() => {
        const pathSegments = window.location.pathname.split('/').filter(Boolean);

        if (pathSegments[0] !== 'doors' || pathSegments.length < 2) {
            return {
                buildingUid: null,
                doorUid: null,
                viewType: null,
                isDoorsPage: false,
                isDoorSpecific: false
            };
        }

        const buildingUid = pathSegments[1];
        const isDoorDetailsPage = pathSegments.length >= 4 &&
            (pathSegments[2] === 'details' || pathSegments[2] === 'resi_details');

        if (isDoorDetailsPage) {
            return {
                buildingUid,
                doorUid: pathSegments[3],
                viewType: pathSegments[2],
                isDoorsPage: true,
                isDoorSpecific: true
            };
        }

        return {
            buildingUid,
            doorUid: null,
            viewType: null,
            isDoorsPage: true,
            isDoorSpecific: false
        };
    }, []);

    const getStorageKey = useCallback((buildingUid, doorUid = null) => {
        return doorUid
            ? `estimate_data_${buildingUid}_door_${doorUid}`
            : `estimate_data_${buildingUid}`;
    }, []);

    const getLocalTimestamp = useCallback(() => {
        return new Date().toLocaleString('sv-SE').replace(',', '');
    }, []);

    const getAccessibleDoors = useCallback(() => {
        const doors = doorsData?.length > 0 ? doorsData : (doorData ? [doorData] : []);
        return doors.filter(door => door.hasAccess !== false);
    }, [doorsData, doorData]);

    const getSelectedDoors = useCallback(() => {
        const accessibleDoors = getAccessibleDoors();
        if (selectedDoorIds.size === 0) return accessibleDoors;
        return accessibleDoors.filter(door => selectedDoorIds.has(door.id || door.doorNumber));
    }, [getAccessibleDoors, selectedDoorIds]);

    const getFilteredDoors = useCallback(() => {
        const accessibleDoors = getAccessibleDoors();
        if (!doorSearchTerm.trim()) return accessibleDoors;

        const searchLower = doorSearchTerm.toLowerCase();
        return accessibleDoors.filter(door => {
            const doorNum = (door.doorNumber || '').toString().toLowerCase();
            const doorLevel = (door.doorLevel || '').toLowerCase();
            const workNeeded = (door.workNeeded || '').toLowerCase();
            return doorNum.includes(searchLower) ||
                doorLevel.includes(searchLower) ||
                workNeeded.includes(searchLower);
        });
    }, [getAccessibleDoors, doorSearchTerm]);

    const handleSelectAllDoors = useCallback(() => {
        savedQuantitiesRef.current = null;
        const accessibleDoors = getAccessibleDoors();
        const allIds = new Set(accessibleDoors.map(d => d.id || d.doorNumber));
        setSelectedDoorIds(allIds);
    }, [getAccessibleDoors]);

    const handleDeselectAllDoors = useCallback(() => {
        savedQuantitiesRef.current = null;
        setSelectedDoorIds(new Set());
    }, []);

    const handleToggleDoor = useCallback((doorId) => {
        savedQuantitiesRef.current = null;
        setSelectedDoorIds(prev => {
            const newSet = new Set(prev);
            newSet.has(doorId) ? newSet.delete(doorId) : newSet.add(doorId);
            return newSet;
        });
    }, []);

    const calculateGapCounts = useCallback((doors) => {
        const gapCounts = {
            "Lock": { minor: 0, major: 0, total: 0 },
            "Hinge": { minor: 0, major: 0, total: 0 },
            "Top": { minor: 0, major: 0, total: 0 },
            "Bottom": { minor: 0, major: 0, total: 0 }
        };

        doors.forEach(door => {
            Object.entries(GAP_MAP).forEach(([fdKey, gapName]) => {
                const raw = door[fdKey];
                if (raw === null || raw === undefined || raw === "") return;

                const gapValue = parseFloat(raw);
                if (Number.isNaN(gapValue)) return;

                const isBottom = gapName.toLowerCase() === "bottom";
                const thresholds = isBottom ? GAP_THRESHOLDS.bottom : GAP_THRESHOLDS.default;

                if (gapValue >= thresholds.major) {
                    gapCounts[gapName].major += 1;
                    gapCounts[gapName].total += 1;
                } else if (gapValue >= thresholds.minor) {
                    gapCounts[gapName].minor += 1;
                    gapCounts[gapName].total += 1;
                }
            });
        });

        return gapCounts;
    }, []);

    const processWorkText = useCallback((workText) => {
        if (!workText?.trim()) return [];
        return workText
            .split(/\n|\|/)
            .map(line => line.trim())
            .filter(Boolean)
            .filter(line => !line.toLowerCase().includes("gap"));
    }, []);

    const normalizeText = useCallback((text) => {
        return text.toLowerCase().replace(/[^a-z0-9\s]/g, "");
    }, []);

    const getAggregatedWorkItems = useCallback(() => {
        const workSummary = {};
        const normalizedMap = {};
        const selectedDoors = getSelectedDoors();
        const gapCounts = calculateGapCounts(selectedDoors);

        const LOCK_NORMALIZED = normalizeText('Replace non-compliant locks');

        const getCountIncrement = (normalizedLine, door) => {
            if (normalizedLine === LOCK_NORMALIZED) {
                const locks = parseInt(door.locksNeeded, 10);
                return locks > 0 ? locks : 1;
            }
            return 1;
        };

        selectedDoors.forEach(door => {
            const workLines = processWorkText(door.workDone || door.workNeeded);
            const localProcessed = new Set();

            workLines.forEach(line => {
                const normalized = normalizeText(line);
                let matched = false;

                for (const existingNorm in normalizedMap) {
                    if (normalized === existingNorm) {
                        const realText = normalizedMap[existingNorm];
                        const key = realText.toLowerCase();

                        if (!workSummary[key]) {
                            workSummary[key] = { text: realText, count: 0 };
                        }
                        if (!localProcessed.has(key)) {
                            workSummary[key].count += getCountIncrement(normalized, door);
                            localProcessed.add(key);
                        }
                        matched = true;
                        break;
                    }
                }

                if (!matched) {
                    const key = line.toLowerCase();
                    normalizedMap[normalized] = line;
                    if (!workSummary[key]) {
                        workSummary[key] = { text: line, count: 0 };
                    }
                    if (!localProcessed.has(key)) {
                        workSummary[key].count += getCountIncrement(normalized, door);
                        localProcessed.add(key);
                    }
                }
            });
        });

        Object.entries(gapCounts).forEach(([gapName, counts]) => {
            if (counts.total === 0) return;

            const text = `Adjust Gap - ${gapName}`;
            const key = text.toLowerCase();

            workSummary[key] = {
                text,
                count: counts.total,
                details: { minor: counts.minor, major: counts.major }
            };
        });

        return workSummary;
    }, [getSelectedDoors, calculateGapCounts, processWorkText, normalizeText]);

    const workItemsMap = useMemo(() => getAggregatedWorkItems(), [getAggregatedWorkItems]);

    const workItems = useMemo(() =>
        Object.values(workItemsMap).map(item => item.text),
        [workItemsMap]
    );

    const customWorkItems = useMemo(() => {
        if (loadedCustomWorkItems.length > 0) {
            return loadedCustomWorkItems;
        }

        const lines = customDescription
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean);

        return lines.map((_, index) => `work${index + 1}`);
    }, [customDescription, loadedCustomWorkItems]);

    const activeWorkItems = useMemo(() =>
        selectedDoorIds.size === 0 ? customWorkItems : workItems,
        [selectedDoorIds.size, customWorkItems, workItems]
    );

    const findPriceMatch = (description, report = null) => {
        const normalized = description.toLowerCase().trim();

        if (pricingTable[normalized]) {
            return pricingTable[normalized];
        }

        if (normalized.includes('gap') || normalized.includes('adjust gap')) {
            const gapType = getGapType(description);

            if (gapType && report) {
                let gapValue = null;

                if (gapType === 'lock' && report.fD1 !== undefined && report.fD1 !== null && report.fD1 !== '') {
                    gapValue = parseFloat(report.fD1);
                } else if (gapType === 'top' && report.fD3 !== undefined && report.fD3 !== null && report.fD3 !== '') {
                    gapValue = parseFloat(report.fD3);
                } else if (gapType === 'hinge' && report.fD2 !== undefined && report.fD2 !== null && report.fD2 !== '') {
                    gapValue = parseFloat(report.fD2);
                } else if (gapType === 'bottom' && report.fD4 !== undefined && report.fD4 !== null && report.fD4 !== '') {
                    gapValue = parseFloat(report.fD4);
                }

                if (gapValue !== null && !isNaN(gapValue)) {
                    const isMajor = gapValue > 6;

                    if (gapType === 'lock') {
                        return isMajor
                            ? { code: 'ADGLm', price: 200 }
                            : { code: 'ADGL', price: 100 };
                    }
                    if (gapType === 'top') {
                        return isMajor
                            ? { code: 'ADGTm', price: 200 }
                            : { code: 'ADGT', price: 100 };
                    }
                    if (gapType === 'hinge') {
                        return isMajor
                            ? { code: 'ADGHm', price: 200 }
                            : { code: 'ADGH', price: 100 };
                    }
                    if (gapType === 'bottom') {
                        return { code: 'ABD', price: 200 };
                    }
                }
            }

            if (normalized.includes('gap')) {
                if (normalized.includes('top')) {
                    if (normalized.includes('major') || normalized.includes('more than 6')) {
                        return { code: 'ADGTm', price: 200 };
                    }
                    return { code: 'ADGT', price: 100 };
                }
                if (normalized.includes('lock')) {
                    if (normalized.includes('major') || normalized.includes('more than 6')) {
                        return { code: 'ADGLm', price: 200 };
                    }
                    return { code: 'ADGL', price: 100 };
                }
                if (normalized.includes('hinge')) {
                    if (normalized.includes('major') || normalized.includes('more than 6')) {
                        return { code: 'ADGHm', price: 200 };
                    }
                    return { code: 'ADGH', price: 100 };
                }
                if (normalized.includes('bottom')) {
                    return { code: 'ABD', price: 200 };
                }
                if (normalized.includes('all')) {
                    if (normalized.includes('major')) return { code: 'AAGm', price: 300 };
                    return { code: 'AAG', price: 200 };
                }
            }
        }

        if ((normalized.includes('intumescent') || normalized.includes('intermittent')) &&
            (normalized.includes('install') || normalized.includes('strip'))) {
            if (normalized.includes('damage')) return { code: 'RDIS', price: 75 };
            return { code: 'INIS', price: 100 };
        }

        if (normalized.includes('third') && normalized.includes('hinge')) {
            return { code: 'ITH', price: 25 };
        }

        if (normalized.includes('signage') || (normalized.includes('install') && normalized.includes('relevant'))) {
            return { code: 'ICS', price: 22.50 };
        }

        if (normalized.includes('screw') && normalized.includes('hinge')) {
            if (normalized.includes('missing')) return { code: 'RMS', price: 25 };
            if (normalized.includes('replace')) return { code: 'MS', price: 25 };
        }

        if (normalized.includes('keyhole') || normalized.includes('key-hole')) {
            return { code: 'RKC', price: 10 };
        }

        if (normalized.includes('closer')) {
            if (normalized.includes('install') || normalized.includes('new')) return { code: 'INDC', price: 200 };
            if (normalized.includes('adjust') || normalized.includes('repair')) return { code: 'ADC', price: 30 };
        }

        if (normalized.includes('lock') && !normalized.includes('gap')) {
            if (normalized.includes('tighten')) return { code: 'TDL', price: 25 };
            if (normalized.includes('replace') || normalized.includes('non-compliant')) return { code: 'RDFL', price: 50 };
            if (normalized.includes('fb 2')) return { code: 'INFB2', price: 115 };
            if (normalized.includes('fb 1')) return { code: 'INFB2', price: 100 };
        }

        if (normalized.includes('latch')) {
            return { code: 'RDL', price: 30 };
        }

        if (normalized.includes('handle')) {
            if (normalized.includes('repair')) return { code: 'RPDH', price: 25 };
            return { code: 'RDH', price: 50 };
        }

        if (normalized.includes('door') && !normalized.includes('gap')) {
            if (normalized.includes('damaged') && normalized.includes('leaf')) return { code: 'RDDL', price: 100 };
            if (normalized.includes('repair') && normalized.includes('damaged')) return { code: 'RDDL', price: 100 };
            if (normalized.includes('replace') && normalized.includes('damaged')) return { code: 'RFD30', price: 650 };
            if (normalized.includes('fd60')) return { code: 'RFD60', price: 800 };
            if (normalized.includes('fd30')) {
                if (normalized.includes('complete') || normalized.includes('set')) {
                    if (normalized.includes('small')) return { code: 'RDS30 (small)', price: 950 };
                    return { code: 'RDS30', price: 1200 };
                }
                return { code: 'RFD30', price: 650 };
            }
        }

        if (normalized.includes('frame')) {
            return { code: 'RDF', price: 80 };
        }

        if (normalized.includes('smoke') && normalized.includes('seal')) {
            if (normalized.includes('fit')) return { code: 'FCSS', price: 100 };
            return { code: 'RCSS', price: 65 };
        }

        if (normalized.includes('drop') && normalized.includes('seal')) {
            return { code: 'IDDS', price: 250 };
        }

        if (normalized.includes('spy')) {
            if (normalized.includes('install')) return { code: 'ISH', price: 75 };
            return { code: 'RSH', price: 25 };
        }

        if (normalized.includes('glass')) {
            return { code: 'RNCG', price: 400 };
        }

        if (normalized.includes('letter')) {
            return { code: 'RLS', price: 100 };
        }

        if (normalized.includes('keyless') || normalized.includes('keyles')) {
            return { code: 'IKE', price: 100 };
        }

        if (normalized.includes('intrusive') || normalized.includes('inspection')) {
            return { code: 'IDI', price: 150 };
        }

        return null;
    };

    const getGapType = (gapString) => {
        const normalized = gapString.toLowerCase();
        if (normalized.includes('lock')) return 'lock';
        if (normalized.includes('top')) return 'top';
        if (normalized.includes('hinge')) return 'hinge';
        if (normalized.includes('bottom')) return 'bottom';
        return null;
    };

    const loadSavedData = async (buildingUid = null, doorUid = null, estimateNo = null) => {
        const urlIds = getIdsFromUrl();
        buildingUid = buildingUid || urlIds.buildingUid;
        doorUid = doorUid !== null ? doorUid : urlIds.doorUid;

        if (!buildingUid || !estimateNo) {
            return null;
        }

        try {
            // const isCustom = buildingUid.startsWith('custom-') || !urlIds.buildingUid;
            const isCustom = buildingUid.startsWith('custom-');

            const subcollectionName = (!doorUid && isResidential)
                ? 'EstimateInfoResi'
                : 'EstimateInfo';

            let estimateInfoRef;
            let buildingEstimateInfoRef = null;

            if (isCustom) {
                estimateInfoRef = doc(db, 'custom-estimates', buildingUid, subcollectionName, estimateNo);
            } else if (doorUid) {
                const isResidentialDoor = doorData?.type === 'residential';
                const doorCollection = isResidentialDoor ? 'ResidentialReports' : 'DoorReports';

                estimateInfoRef = doc(db, 'Reports', buildingUid, doorCollection, doorUid, 'EstimateInfo', estimateNo);

                const buildingSubcollection = isResidential ? 'EstimateInfoResi' : 'EstimateInfo';
                buildingEstimateInfoRef = doc(db, 'Reports', buildingUid, buildingSubcollection, estimateNo);
            } else {
                estimateInfoRef = doc(db, 'Reports', buildingUid, subcollectionName, estimateNo);
            }

            const estimateDoc = await getDoc(estimateInfoRef);

            if (estimateDoc.exists()) {
                return {
                    ...estimateDoc.data(),
                    ...(doorUid && {
                        _isDoorSpecific: true,
                        _doorUid: doorUid
                    })
                };
            }

            if (doorUid && buildingEstimateInfoRef) {
                const buildingEstimateDoc = await getDoc(buildingEstimateInfoRef);

                if (buildingEstimateDoc.exists()) {
                    return {
                        ...buildingEstimateDoc.data(),
                        _isDoorSpecific: false,
                        _doorUid: doorUid,
                        _loadedFromBuilding: true
                    };
                }
            }

            return null;
        } catch (error) {
            console.error('Error loading saved data:', error);
            return null;
        }
    };

    const saveDataToFirestore = async (buildingUid = null, doorUid = null, estimateNo = null) => {
        const urlIds = getIdsFromUrl();

        buildingUid = buildingUid || urlIds.buildingUid || customEstimateData?.id;
        doorUid = doorUid !== null ? doorUid : urlIds.doorUid;

        if (!buildingUid || !estimateNo) {
            return;
        }

        try {
            const currentTimestamp = new Date().toLocaleString('sv-SE').replace(',', '');

            const isCustom = selectedDoorIds.size === 0;
            const isBuildingLevel = !doorUid && !isCustom;

            const subcollectionName = (isBuildingLevel && isResidential) ? 'EstimateInfoResi' : 'EstimateInfo';

            let estimateInfoRef;

            if (isCustom) {
                estimateInfoRef = doc(db, 'custom-estimates', buildingUid, subcollectionName, estimateNo);
            } else if (doorUid) {
                const isResidentialDoor = doorData?.type === 'residential';
                const doorCollection = isResidentialDoor ? 'ResidentialReports' : 'DoorReports';

                estimateInfoRef = doc(db, 'Reports', buildingUid, doorCollection, doorUid, 'EstimateInfo', estimateNo);
            } else {
                estimateInfoRef = doc(db, 'Reports', buildingUid, subcollectionName, estimateNo);
            }

            const existingDoc = await getDoc(estimateInfoRef);
            const isExistingEstimate = existingDoc.exists();

            const dataToSave = {
                estimateNo: estimateNo,
                total: totals.total,
                payeeName: payeeDetails.payeeName,
                ...(!isExistingEstimate && { createdAt: currentTimestamp }),
                updatedAt: currentTimestamp,
                prices,
                codes,
                quantities,
                doorRefs,
                workItemDescriptions,
                selectedWorkItems: Array.from(selectedWorkItems),
                laborCost,
                includeVAT,
                includeManagementFee,
                clientDetails,
                payeeDetails,
                propertyDetails,
                discountValue: totals.discountPercentage,
                discountAmount: totals.discount,
                discountType: totals.discountPercentage > 0
                    ? String(totals.discountPercentage)
                    : 'none',
            };

            if (doorUid && !isCustom) {
                dataToSave.doorUid = doorUid;
            }

            if (!doorUid && !isCustom) {
                dataToSave.doorCount = selectedDoorIds.size;
            }

            await setDoc(estimateInfoRef, dataToSave, { merge: true });
        } catch (error) {
            console.error('Error saving data to Firestore:', error);
        }
    };

    const fetchAndIncrementEstimateNumber = useCallback(async () => {
        try {
            const isSingleDoor = doorData !== null;
            const isEditingCustom = customEstimateData !== null;

            const suffix = (!isSingleDoor && isResidential) ? 'Resi' : '';

            const existingEstimateNo = isEditingCustom
                ? customEstimateData?.[`estimateNo${suffix}`]
                : isSingleDoor
                    ? doorData?.estimateNo
                    : buildingData?.[`estimateNo${suffix}`];

            if (existingEstimateNo) {
                setEstimateNumber(existingEstimateNo);
                return null;
            }

            const metadataRef = doc(db, 'metadata', 'UkTkEj0tgn25LwIh4zWb');
            const metadataDoc = await getDoc(metadataRef);

            const lastEstimateNumber = metadataDoc.exists()
                ? (metadataDoc.data().lastEstimateNumber || 0)
                : 0;

            const newEstimateNumber = lastEstimateNumber + 1;
            const estimateNo = `EST-${new Date().getFullYear()}-${String(newEstimateNumber).padStart(4, '0')}`;

            setEstimateNumber(estimateNo);
            return { metadataRef, newEstimateNumber };
        } catch (error) {
            console.error('Error fetching estimate number:', error);
            setEstimateNumber(`EST-${new Date().getFullYear()}-0001`);
            return null;
        }
    }, [doorData, customEstimateData, buildingData, isResidential]);

    const updateEstimateNumber = useCallback(async (metadataRef, newEstimateNumber) => {
        try {
            const isSingleDoor = doorData !== null;
            const suffix = isResidential ? 'Resi' : '';

            const existingEstimateNo = isSingleDoor
                ? doorData?.[`estimateNo${suffix}`]
                : buildingData?.[`estimateNo${suffix}`];

            if (!existingEstimateNo) {
                await updateDoc(metadataRef, {
                    lastEstimateNumber: newEstimateNumber
                });
            }
        } catch (error) {
            console.error('Error updating estimate number:', error);
        }
    }, [doorData, buildingData, isResidential]);

    const populateEstimateData = useCallback((loadedData) => {
        setIsLoadingExistingEstimate(true);

        const allWorkItems = Object.keys(
            loadedData.prices || loadedData.doorRefs || loadedData.codes || {}
        );

        const sortedWorkItems = allWorkItems.sort((a, b) => {
            const numA = parseInt(a.replace('work', ''));
            const numB = parseInt(b.replace('work', ''));
            return numA - numB;
        });

        const descriptions = loadedData.workItemDescriptions || {};
        const lines = sortedWorkItems.map(item => descriptions[item] || item);

        setCustomDescription(lines.join('\n'));
        setWorkItemDescriptions(descriptions);
        setLoadedCustomWorkItems(sortedWorkItems);

        const initialPrices = {};
        const initialCodes = {};
        const initialQuantities = {};
        const initialDoorRefs = {};

        allWorkItems.forEach((item) => {
            initialPrices[item] = loadedData.prices?.[item] ?? '0';
            initialCodes[item] = loadedData.codes?.[item] ?? 'ITEM1';
            initialQuantities[item] = loadedData.quantities?.[item] ?? '0';
            initialDoorRefs[item] = loadedData.doorRefs?.[item] || '';
        });

        setPrices(initialPrices);
        setCodes(initialCodes);
        setQuantities(initialQuantities);
        setDoorRefs(initialDoorRefs);

        if (loadedData.selectedWorkItems && Array.isArray(loadedData.selectedWorkItems)) {
            setSelectedWorkItems(new Set(loadedData.selectedWorkItems));
        } else {
            setSelectedWorkItems(new Set(allWorkItems));
        }

        setLaborCost(loadedData.laborCost || '0');
        setIncludeVAT(loadedData.includeVAT ?? true);
        setIncludeManagementFee(loadedData.includeManagementFee ?? false);
        setDiscountValue(getSavedDiscountValue(loadedData));

        setTimeout(() => setIsLoadingExistingEstimate(false), 100);
    }, []);

    const resetEstimateFields = useCallback(() => {
        setCustomDescription('');
        setLoadedCustomWorkItems([]);
        setPrices({});
        setCodes({});
        setQuantities({});
        setDoorRefs({});
        setSelectedWorkItems(new Set());
        setLaborCost('0');
        setIncludeVAT(true);
        setIncludeManagementFee(false);
        setDiscountValue('');
    }, []);

    const populateClientDetails = useCallback((data) => {
        setClientDetails({
            clientName: data.clientName || '',
            clientEmail: data.email || data.clientEmail || '',
            clientAccountEmail: data.clientAccountEmail || '',
            clientTelephone: data.clientTelephone || '',
        });

        setPayeeDetails({
            payeeName: data.payee || data.payeeName || '',
            invoiceAddress: data.estimateInfoData?.payeeDetails?.invoiceAddress || data.payeeDetails?.invoiceAddress || '',
            invoiceAddress2: data.estimateInfoData?.payeeDetails?.invoiceAddress2 || data.payeeDetails?.invoiceAddress2 || '',
            invoiceAddress3: data.estimateInfoData?.payeeDetails?.invoiceAddress3 || data.payeeDetails?.invoiceAddress3 || '',
            invoicePostcode: data.estimateInfoData?.payeeDetails?.invoicePostcode || data.payeeDetails?.invoicePostcode || '',
        });

        setPropertyDetails({
            propertyAddress: data.estimateInfoData?.propertyDetails?.propertyAddress || data.propertyDetails?.propertyAddress || data.buildingName || '',
            propertyAddress2: data.estimateInfoData?.propertyDetails?.propertyAddress2 || data.propertyDetails?.propertyAddress2 || data.clientAddress || '',
            propertyAddress3: data.estimateInfoData?.propertyDetails?.propertyAddress3 || data.propertyDetails?.propertyAddress3 || data.city || '',
            propertyPostcode: data.estimateInfoData?.propertyDetails?.propertyPostcode || data.propertyDetails?.propertyPostcode || data.postcode || '',
            notes: data.estimateInfoData?.propertyDetails?.notes || data.propertyDetails?.notes || '',
        });
    }, []);

    const initializeBuildingEstimateData = useCallback(async (isSingleDoor) => {
        if (workItems.length === 0) return;

        const suffix = (!isSingleDoor && isResidential) ? 'Resi' : '';

        let estimateNo = isSingleDoor
            ? doorData?.estimateNo
            : buildingData?.[`estimateNo${suffix}`];

        if (!estimateNo && !isSingleDoor && buildingData?.uid) {
            try {
                const buildingDocRef = doc(db, 'Reports', buildingData.uid);
                const buildingDoc = await getDoc(buildingDocRef);

                if (buildingDoc.exists()) {
                    const freshData = buildingDoc.data();
                    estimateNo = freshData[`estimateNo${suffix}`];
                }
            } catch (error) {
                console.error('Error fetching building data:', error);
            }
        }

        if (!estimateNo && isSingleDoor && doorData?.uid && buildingData?.uid) {
            try {
                const doorCollection = doorData?.type === 'residential' ? 'ResidentialReports' : 'DoorReports';
                const doorDocRef = doc(db, 'Reports', buildingData.uid, doorCollection, doorData.uid);
                const doorDoc = await getDoc(doorDocRef);

                if (doorDoc.exists()) {
                    const freshDoorData = doorDoc.data();
                    estimateNo = freshDoorData.estimateNo;
                }
            } catch (error) {
                console.error('Error fetching door data:', error);
            }
        }

        if (estimateNo) {
            const loadedData = await loadSavedData(
                buildingData?.uid,
                isSingleDoor ? doorData.uid : null,
                estimateNo
            );

            if (loadedData) {
                savedQuantitiesRef.current = loadedData.quantities || null;

                const initialPrices = {};
                const initialCodes = {};
                const initialQuantities = {};

                workItems.forEach((item, index) => {
                    const key = item.toLowerCase();
                    const priceMatch = findPriceMatch(item);

                    if (loadedData.prices?.[item] !== undefined && loadedData.prices?.[item] !== null && loadedData.prices?.[item] !== '') {
                        initialPrices[item] = loadedData.prices[item];
                    } else if (priceMatch?.price) {
                        initialPrices[item] = priceMatch.price.toString();
                    } else {
                        initialPrices[item] = '0';
                    }

                    const savedCode = loadedData.codes?.[item];
                    if (savedCode && savedCode.trim() !== '') {
                        initialCodes[item] = savedCode;
                    } else if (priceMatch?.code) {
                        initialCodes[item] = priceMatch.code;
                    } else {
                        initialCodes[item] = workItemsMap[key]?.code || `ITEM${index + 1}`;
                    }

                    initialQuantities[item] = loadedData.quantities?.[item] ?? (workItemsMap[key]?.count?.toString() || '0');
                });

                setPrices(initialPrices);
                setCodes(initialCodes);
                setQuantities(initialQuantities);

                if (loadedData.doorRefs) setDoorRefs(loadedData.doorRefs);
                if (loadedData.selectedWorkItems) {
                    setSelectedWorkItems(new Set(loadedData.selectedWorkItems));
                }

                setLaborCost(loadedData.laborCost || '0');
                setIncludeVAT(loadedData.includeVAT ?? true);
                setIncludeManagementFee(loadedData.includeManagementFee ?? false);
                setDiscountValue(getSavedDiscountValue(loadedData));

                populateClientDetails(loadedData);
                setEstimateNumber(estimateNo);
                return;
            }
        }

        await fetchAndIncrementEstimateNumber();
        populateClientDetails(buildingData);

        const initialPrices = {};
        const initialCodes = {};
        const initialQuantities = {};

        workItems.forEach((item, index) => {
            const key = item.toLowerCase();
            const priceMatch = findPriceMatch(item);

            initialPrices[item] = priceMatch?.price?.toString() || '0';
            initialCodes[item] = priceMatch?.code || `ITEM${index + 1}`;
            initialQuantities[item] = workItemsMap[key]?.count?.toString() || '0';
        });

        setPrices(initialPrices);
        setCodes(initialCodes);
        setQuantities(initialQuantities);
        setLaborCost('0');
        setIncludeVAT(true);
        setIncludeManagementFee(false);
        setDiscountValue('');
    }, [
        workItems, doorData, buildingData, workItemsMap, isResidential,
        loadSavedData, fetchAndIncrementEstimateNumber, populateClientDetails
    ]);

    useEffect(() => {
        if (customDescription && loadedCustomWorkItems.length === 0 && !isLoadingExistingEstimate) {
            const lines = customDescription
                .split('\n')
                .map(line => line.trim())
                .filter(Boolean);

            const newDescriptions = {};
            const newPrices = {};
            const newCodes = {};
            const newQuantities = {};

            lines.forEach((line, index) => {
                const key = `work${index + 1}`;
                newDescriptions[key] = line;

                if (!prices[key]) newPrices[key] = '0';
                if (!codes[key]) newCodes[key] = `ITEM${index + 1}`;
                if (!quantities[key]) newQuantities[key] = '0';
            });

            setWorkItemDescriptions(prev => ({ ...prev, ...newDescriptions }));
            setPrices(prev => ({ ...prev, ...newPrices }));
            setCodes(prev => ({ ...prev, ...newCodes }));
            setQuantities(prev => ({ ...prev, ...newQuantities }));

            setSelectedWorkItems(prev => {
                const newWorkItems = lines.map((_, index) => `work${index + 1}`);
                const newSet = new Set(prev);

                newWorkItems.forEach(item => {
                    if (!prices[item]) {
                        newSet.add(item);
                    }
                });

                return newSet;
            });
        }
    }, [customDescription, loadedCustomWorkItems.length, isLoadingExistingEstimate]);

    useEffect(() => {
        if (show && selectedDoorIds.size === 0) {
            setIncludeVAT(true);
            setIncludeManagementFee(false);
        }
    }, [show, selectedDoorIds.size]);

    useEffect(() => {
        if (show && selectedDoorIds.size === 0 && !customEstimateData) {
            fetchAndIncrementEstimateNumber();
        }
    }, [show, selectedDoorIds.size, customEstimateData, fetchAndIncrementEstimateNumber]);

    useEffect(() => {
        if (show && customEstimateData) {
            setSelectedDoorIds(new Set());

            if (customEstimateData.estimateNo) {
                setEstimateNumber(customEstimateData.estimateNo);
            }

            populateClientDetails(customEstimateData);

            if (customEstimateData.estimateInfoData) {
                const loadedData = customEstimateData.estimateInfoData;

                if (loadedData.workItemDescriptions) {
                    setWorkItemDescriptions(loadedData.workItemDescriptions);
                }

                const savedSelectedItems = loadedData.selectedWorkItems || [];
                setSelectedWorkItems(new Set(savedSelectedItems));
                setLoadedCustomWorkItems(savedSelectedItems);

                if (loadedData.prices) setPrices(loadedData.prices);
                if (loadedData.codes) setCodes(loadedData.codes);
                if (loadedData.quantities) setQuantities(loadedData.quantities);
                if (loadedData.doorRefs) setDoorRefs(loadedData.doorRefs);

                const descriptions = loadedData.workItemDescriptions || {};
                const sortedWorkItems = savedSelectedItems.sort((a, b) => {
                    const numA = parseInt(a.replace('work', ''));
                    const numB = parseInt(b.replace('work', ''));
                    return numA - numB;
                });
                const lines = sortedWorkItems.map(item => descriptions[item] || item);
                setCustomDescription(lines.join('\n'));

                setLaborCost(loadedData.laborCost || '0');
                setIncludeVAT(loadedData.includeVAT ?? true);
                setIncludeManagementFee(loadedData.includeManagementFee ?? false);
                setDiscountValue(getSavedDiscountValue(loadedData));
            }

            setActiveTab('work');
        } else if (show && !customEstimateData) {
            setLoadedCustomWorkItems([]);
        }
    }, [show, customEstimateData, populateClientDetails]);

    useEffect(() => {
        if (!show) {
            prevShowRef.current = false;
            hasInitializedRef.current = false;
            prevBuildingUidRef.current = null;
            savedQuantitiesRef.current = null;
            setIsLoadingExistingEstimate(false);

            setActiveTab('details');
            setIsDoorSelectorOpen(false);
            setDoorSearchTerm('');
            setExportNewLayout(false);
            setShowDoorSelector(false);

            setPrices({});
            setCodes({});
            setQuantities({});
            setDoorRefs({});
            setSelectedWorkItems(new Set());
            setWorkItemDescriptions({});
            setLoadedCustomWorkItems([]);
            setCustomDescription('');
            setCustomDoorRef('');

            setLaborCost('0');
            setDiscountValue('');
            setIncludeVAT(false);
            setIncludeManagementFee(false);

            setEstimateNumber('');
            setSelectedDoorIds(new Set());

            setClientDetails({
                clientName: '',
                clientEmail: '',
                clientAccountEmail: '',
                clientTelephone: '',
            });
            setPayeeDetails({
                payeeName: '',
                invoiceAddress: '',
                invoiceAddress2: '',
                invoiceAddress3: '',
                invoicePostcode: '',
            });
            setPropertyDetails({
                propertyAddress: '',
                propertyAddress2: '',
                propertyAddress3: '',
                propertyPostcode: '',
            });

            return;
        }

        if (prevShowRef.current) return;
        prevShowRef.current = true;

        setActiveTab('details');
        setIsDoorSelectorOpen(false);
        setDoorSearchTerm('');

        if (buildingData) {
            const accessibleDoors = getAccessibleDoors();
            const allDoorIds = new Set(accessibleDoors.map(d => d.id || d.doorNumber));
            setSelectedDoorIds(allDoorIds);
            setSelectedWorkItems(new Set());
        }
    }, [show, buildingData, getAccessibleDoors]);

    useEffect(() => {
        if (!show || !buildingData) return;

        const buildingUid = buildingData?.uid;

        if (hasInitializedRef.current && prevBuildingUidRef.current === buildingUid) return;
        hasInitializedRef.current = true;
        prevBuildingUidRef.current = buildingUid;

        const isSingleDoor = doorData !== null;
        const suffix = (!isSingleDoor && isResidential) ? 'Resi' : '';

        const hasExistingEstimate = isSingleDoor
            ? doorData?.estimateNo
            : buildingData?.[`estimateNo${suffix}`];

        if (!hasExistingEstimate) {
            setSelectedWorkItems(new Set());
            setPrices({});
            setCodes({});
            setQuantities({});
            setDoorRefs({});
            setCustomDescription('');
            setLoadedCustomWorkItems([]);
            setWorkItemDescriptions({});
        }

        if (workItems.length > 0) {
            initializeBuildingEstimateData(isSingleDoor);
        }

        const doors = doorsData?.length > 0 ? doorsData : (doorData ? [doorData] : []);
        const accessibleDoors = doors.filter(door => door.hasAccess !== false);
        setShowDoorSelector(accessibleDoors.length > 1);
    }, [show, buildingData, doorData, doorsData, workItems.length, isResidential, initializeBuildingEstimateData]);

    useEffect(() => {
        if (show && !buildingData && customEstimateData) {
            populateClientDetails(customEstimateData);
            setEstimateNumber(customEstimateData.estimateNo || '');

            const loadEstimateInfo = async () => {
                if (customEstimateData.estimateInfoData) {
                    populateEstimateData(customEstimateData.estimateInfoData);
                } else if (customEstimateData.id && customEstimateData.estimateNo) {
                    try {
                        const estimateInfoSnapshot = await getDocs(
                            collection(db, `custom-estimates/${customEstimateData.id}/EstimateInfo`)
                        );

                        if (!estimateInfoSnapshot.empty) {
                            const firstEstimateDoc = estimateInfoSnapshot.docs[0];
                            populateEstimateData(firstEstimateDoc.data());
                        } else {
                            fetchAndIncrementEstimateNumber();
                            resetEstimateFields();
                        }
                    } catch (error) {
                        console.error('Error loading estimate info:', error);
                        resetEstimateFields();
                    }
                } else {
                    fetchAndIncrementEstimateNumber();
                    resetEstimateFields();
                }
            };

            loadEstimateInfo();
        }
    }, [show, buildingData, customEstimateData, populateClientDetails, populateEstimateData, fetchAndIncrementEstimateNumber, resetEstimateFields]);

    useEffect(() => {
        if (show && selectedDoorIds.size > 0) {
            const updatedWorkItemsMap = getAggregatedWorkItems();
            const saved = savedQuantitiesRef.current;
            const newQuantities = {};

            Object.values(updatedWorkItemsMap).forEach(item => {
                newQuantities[item.text] =
                    saved?.[item.text] ?? (item.count?.toString() || '0');
            });

            setQuantities(newQuantities);
        }
    }, [selectedDoorIds, show, getAggregatedWorkItems]);

    useEffect(() => {
        if (selectedDoorIds.size === 0 && customWorkItems.length > 0) {
            setPrices(prev => {
                const next = {};
                customWorkItems.forEach(item => {
                    next[item] = prev[item] !== undefined ? prev[item] : '0';
                });
                return next;
            });

            setCodes(() => {
                const next = {};
                customWorkItems.forEach((item, index) => {
                    next[item] = `ITEM${index + 1}`;
                });
                return next;
            });

            setQuantities(prev => {
                const next = {};
                customWorkItems.forEach(item => {
                    next[item] = prev[item] !== undefined ? prev[item] : '1';
                });
                return next;
            });

            setDoorRefs(prev => {
                const next = {};
                customWorkItems.forEach(item => {
                    next[item] = customDoorRef || prev[item] || '';
                });
                return next;
            });
        }
    }, [customDescription, customDoorRef, selectedDoorIds.size, customWorkItems]);

    const isNextDisabled =
        !payeeDetails.payeeName?.trim() ||
        !payeeDetails.invoiceAddress?.trim() ||
        !payeeDetails.invoicePostcode?.trim() ||
        !propertyDetails.propertyPostcode?.trim() ||
        !propertyDetails.propertyAddress?.trim();

    const isFieldInvalid = (value) => !value?.trim();

    const calculateTotals = () => {
        const itemsTotal = Object.keys(prices)
            .filter(item => selectedWorkItems.has(item))
            .reduce((sum, item) => {
                const price = parseFloat(prices[item]) || 0;
                const qty = parseFloat(quantities[item]) || 1;
                return sum + (price * qty);
            }, 0);

        const labor = parseFloat(laborCost) || 0;

        let subtotal = itemsTotal + labor;

        const value = Math.max(0, Number(discountValue) || 0);

        const discount = discountType === 'amount'
            ? Math.min(subtotal, value)
            : subtotal * Math.min(100, value) / 100;

        const discountPercentage = subtotal > 0
            ? (discount / subtotal) * 100
            : 0;

        subtotal -= discount;

        const managementFee = includeManagementFee
            ? subtotal * 0.15
            : 0;

        const subtotalWithFee = subtotal + managementFee;

        const vat = includeVAT
            ? subtotalWithFee * 0.20
            : 0;

        const total = subtotalWithFee + vat;

        return {
            itemsTotal,
            labor,
            discount,
            discountPercentage,
            subtotal,
            managementFee,
            vat,
            total
        };
    };

    const totals = calculateTotals();

    useEffect(() => {
        if (!isEditingDiscount) {
            setDiscountAmountInput(totals.discount.toFixed(2));
        }
    }, [totals.discount, isEditingDiscount]);

    const saveEstimate = async (pdfBlob, estimateNumber, total, payeeName, customEstimateId = null) => {
        try {
            const now = new Date();
            const formattedDate = format(now, "yyyy-MM-dd HH:mm:ss");

            let doorDocRef;
            let storagePath;

            const isCustom = selectedDoorIds.size === 0;
            const isSingleDoor = doorData !== null;

            if (isCustom) {
                if (customEstimateId) {
                    doorDocRef = doc(db, "custom-estimates", customEstimateId);
                    storagePath = `custom-estimates/${customEstimateId}`;
                } else {
                    const customDocRef = doc(collection(db, "custom-estimates"));
                    doorDocRef = customDocRef;
                    storagePath = `custom-estimates/${customDocRef.id}`;
                }
            } else if (doorData) {
                if (!doorData.type || !doorData.uid) {
                    return null;
                }

                const buildingUid = buildingData?.uid || foundBuildingUid;

                if (!buildingUid) {
                    return null;
                }

                if (doorData.type === 'communal') {
                    doorDocRef = doc(db, "Reports", buildingUid, "DoorReports", doorData.uid);
                    storagePath = `Reports/${buildingUid}/DoorReports/${doorData.uid}`;
                } else if (doorData.type === 'residential') {
                    doorDocRef = doc(db, "Reports", buildingUid, "ResidentialReports", doorData.uid);
                    storagePath = `Reports/${buildingUid}/ResidentialReports/${doorData.uid}`;
                }
            } else {
                if (!buildingData?.uid) {
                    return null;
                }
                doorDocRef = doc(db, "Reports", buildingData.uid);
                storagePath = `Reports/${buildingData.uid}`;
            }

            const isBuildingLevel = !doorData && !isCustom;

            const suffix = (isBuildingLevel && isResidential) ? 'Resi' : '';

            const filename = `estimate${suffix}.pdf`;
            const storageRef = ref(storage, `${storagePath}/${filename}`);

            await uploadBytes(storageRef, pdfBlob);

            const downloadURL = await getDownloadURL(storageRef);

            const data = {
                [`estimateDate${suffix}`]: formattedDate,
                [`estimatePDF${suffix}`]: downloadURL,
                [`estimateNo${suffix}`]: estimateNumber,
                [`estimateTotal${suffix}`]: total,
                [`estimatePayee${suffix}`]: payeeName,
                ...(isCustom && {
                    uid: doorDocRef.id,
                    clientName: clientDetails.clientName || '',
                    clientEmail: clientDetails.clientEmail || '',
                    clientAccountEmail: clientDetails.clientAccountEmail || '',
                    clientTelephone: clientDetails.clientTelephone || '',
                    payeeName: payeeDetails.payeeName || '',
                }),
            };

            await setDoc(doorDocRef, data, { merge: true });

            return { downloadURL, docId: doorDocRef.id };
        } catch (error) {
            console.error("Error saving estimate:", error);
            return null;
        }
    };

    const handleGenerateEstimate = async () => {
        if (selectedDoorIds.size === 0 && customWorkItems.length === 0) {
            alert("Please select doors or enter a custom description to generate an estimate.");
            return;
        }

        setLoading(true);
        try {
            const itemsWithPrices = activeWorkItems
                .filter(item => selectedWorkItems.has(item))
                .map(item => ({
                    code: codes[item] || '-',
                    door: doorRefs[item] || customDoorRef || '-',
                    description: workItemDescriptions[item] || item,
                    price: parseFloat(prices[item]) || 0,
                    quantity: parseFloat(quantities[item]) || 1
                }));

            const selectedDoors = getSelectedDoors();

            const doorsWithSelectedWork = selectedDoors.filter(door => {
                const doorWorkItems = activeWorkItems.filter(item => {
                    return selectedWorkItems.has(item);
                });

                return doorWorkItems.length > 0;
            });

            const sortedDoors = doorsWithSelectedWork
                .map(door => ({
                    ...door,
                    doorNumber: door.doorNumber?.toString() || '',
                    workNeeded: door.workDone || door.workNeeded || '',
                }))
            // .sort((a, b) => {
            //     const numA = parseInt(a.doorNumber) || 0;
            //     const numB = parseInt(b.doorNumber) || 0;
            //     if (numA !== numB) return numA - numB;
            //     return a.doorNumber.localeCompare(b.doorNumber);
            // });

            const getCustomDoorType = (doorNum) => {
                const prefix = doorNum?.toString().trim().toUpperCase().charAt(0);
                if (prefix === 'R') return 'residential';
                if (prefix === 'C') return 'communal';
                return 'communal';
            };

            const getCleanDoorNumber = (doorNum) => {
                return doorNum?.toString().replace(/^[a-zA-Z]+/, '').trim() || '-';
            };

            const customDoors = selectedDoorIds.size === 0
                ? [...new Set(Object.values(doorRefs).filter(Boolean))]
                    .map(dn => ({
                        doorNumber: getCleanDoorNumber(dn),
                        type: getCustomDoorType(dn),
                        _raw: dn,
                        workNeeded: activeWorkItems
                            .filter(item => selectedWorkItems.has(item) && doorRefs[item] === dn)
                            .map(item => workItemDescriptions[item] || item)
                            .join('\n')
                    }))
                    .filter(door => door.workNeeded.trim().length > 0)
                    .concat(customDoorRef && !Object.values(doorRefs).includes(customDoorRef)
                        ? [{
                            doorNumber: getCleanDoorNumber(customDoorRef),
                            type: getCustomDoorType(customDoorRef),
                            _raw: customDoorRef,
                            workNeeded: activeWorkItems
                                .filter(item => selectedWorkItems.has(item))
                                .map(item => workItemDescriptions[item] || item)
                                .join('\n')
                        }]
                        : []
                    ) || [{ doorNumber: '-', type: 'communal', workNeeded: '' }]
                : [];

            const payload = {
                estimateNumber: estimateNumber,
                doors: selectedDoorIds.size === 0
                    ? customDoors.map(({ _raw, ...d }) => d)
                    : sortedDoors,
                door: selectedDoorIds.size === 0
                    ? customDoors.length === 1
                        ? (({ _raw, ...d }) => d)(customDoors[0])
                        : null
                    : doorData && selectedDoorIds.has(doorData.id || doorData.doorNumber)
                        ? { ...doorData }
                        : null,
                exportNewLayout: exportNewLayout,
                report: {
                    ...(buildingData || {}),
                    clientName: clientDetails.clientName,
                    clientEmail: clientDetails.clientEmail,
                    clientAccountEmail: clientDetails.clientAccountEmail,
                    clientTelephone: clientDetails.clientTelephone,
                    payee: payeeDetails.payeeName,
                    invoiceAddress: payeeDetails.invoiceAddress,
                    invoiceAddress2: payeeDetails.invoiceAddress2,
                    invoiceAddress3: payeeDetails.invoiceAddress3,
                    invoicePostcode: payeeDetails.invoicePostcode,
                    buildingAddress: propertyDetails.propertyAddress,
                    buildingAddress2: propertyDetails.propertyAddress2,
                    buildingAddress3: propertyDetails.propertyAddress3,
                    buildingPostcode: propertyDetails.propertyPostcode,
                    notes: propertyDetails.notes,
                },
                pricing: {
                    items: itemsWithPrices,
                    laborCost: parseFloat(laborCost) || 0,
                    discountValue: totals.discountPercentage.toFixed(2),
                    discountAmount: totals.discount,
                    includeVAT: includeVAT,
                    includeManagementFee: includeManagementFee,
                    subtotal: totals.subtotal,
                    managementFee: totals.managementFee,
                    vat: totals.vat,
                    total: totals.total,
                }
            };

            const response = await fetch(
                "https://firechecked-gcp-813451531907.europe-west1.run.app/export-estimate-pdf",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Server error:", errorText);
                throw new Error(`Failed to generate PDF: ${response.statusText}`);
            }

            const blob = await response.blob();
            const pdfUrl = window.URL.createObjectURL(blob);
            window.open(pdfUrl, "_blank");

            let estimateData = null;

            const isSingleDoor = doorData !== null;
            const suffix = (!isSingleDoor && isResidential) ? 'Resi' : '';

            const hasExistingEstimate = customEstimateData?.estimateNo ||
                (isSingleDoor ? doorData?.estimateNo : buildingData?.[`estimateNo${suffix}`]);

            if (!hasExistingEstimate) {
                estimateData = await fetchAndIncrementEstimateNumber();
            }

            const resolvedEstimateNumber = estimateNumber;

            if (estimateData) {
                await updateEstimateNumber(estimateData.metadataRef, estimateData.newEstimateNumber);
            }

            const customEstimateId = customEstimateData?.id || null;
            const saveResult = await saveEstimate(blob, resolvedEstimateNumber, totals.total, payeeDetails.payeeName, customEstimateId);
            const isCustom = selectedDoorIds.size === 0;

            if (isCustom) {
                if (saveResult?.docId) {
                    await saveDataToFirestore(saveResult.docId, null, resolvedEstimateNumber);
                }
            } else if (buildingData?.uid) {
                const isSingleDoor = doorData !== null;

                if (isSingleDoor) {
                    await saveDataToFirestore(buildingData.uid, doorData.uid, resolvedEstimateNumber);
                } else {
                    await saveDataToFirestore(buildingData.uid, null, resolvedEstimateNumber);
                }
            }

            if (onGenerate) onGenerate();
            onHide();

            // window.location.reload();
        } catch (error) {
            console.error("Error generating estimate:", error);
            alert("Failed to generate estimate. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleClientDetailsChange = (field, value) => {
        setClientDetails(prev => ({ ...prev, [field]: value }));
    };

    const handlePayeeDetailsChange = (field, value) => {
        setPayeeDetails(prev => ({ ...prev, [field]: value }));
    };

    const handlePropertyDetailsChange = (field, value) => {
        setPropertyDetails(prev => ({ ...prev, [field]: value }));
    };

    const customStyles = {
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        content: {
            position: 'relative',
            top: 'auto',
            left: 'auto',
            right: 'auto',
            bottom: 'auto',
            width: '1200px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflowY: "auto",
            padding: '0',
            border: 'none',
            borderRadius: '8px',
            overflow: 'hidden',
        }
    };

    const accessibleDoors = getAccessibleDoors();
    const filteredDoors = getFilteredDoors();

    const parseDoorNumber = (doorNumber) => {
        const str = String(doorNumber || "").replace(/\s+/g, "");
        const match = str.match(/^([A-Za-z]*)(\d+)?([A-Za-z]*)$/);
        if (!match) return { num: Infinity, category: 3, key: str };

        const [, prefix, digits, suffix] = match;
        const num = digits ? parseInt(digits, 10) : Infinity;

        let category;
        if (!prefix && !suffix) category = 0;
        else if (!prefix && suffix) category = 1;
        else category = 2;

        return { num, category, key: prefix + suffix };
    };

    const sortedDoors = filteredDoors.sort((a, b) => {
        const A = parseDoorNumber(a.doorNumber);
        const B = parseDoorNumber(b.doorNumber);

        if (A.num !== B.num) return A.num - B.num;
        if (A.category !== B.category) return A.category - B.category;
        return A.key.localeCompare(B.key);
    });

    return (
        <Modal
            isOpen={show}
            onRequestClose={onHide}
            style={customStyles}
            contentLabel="Generate Estimate"
        >
            <style>
                {`
                    .estimate-modal-input:focus {
                        border-color: #dc3545 !important;
                        box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
                    }
                    .estimate-modal-input-group:focus-within .input-group-text {
                        border-color: #dc3545 !important;
                    }
                    .door-selector-toggle {
                        cursor: pointer;
                        transition: all 0.2s ease;
                        user-select: none;
                    }
                    .door-selector-toggle:hover {
                        background-color: #f8f9fa !important;
                    }
                    .door-item {
                        cursor: pointer;
                        transition: all 0.15s ease;
                        padding: 10px;
                        border-radius: 6px;
                        border: 1px solid #dee2e6;
                        background: white;
                    }
                    .door-item:hover {
                        background-color: #f8f9fa;
                        border-color: #adb5bd;
                        transform: translateY(-1px);
                        box-shadow: 0 2px 4px rgba(0,0,0,0.08);
                    }
                    .door-item.selected {
                        background-color: #e7f5ec;
                        border-color: #28a745;
                        box-shadow: 0 0 0 1px rgba(40, 167, 69, 0.2);
                    }
                    .door-item.selected:hover {
                        background-color: #d4f0dd;
                        border-color: #1e7e34;
                        box-shadow: 0 2px 6px rgba(40, 167, 69, 0.15);
                    }
                    .nav-tabs .nav-link {
                        color: #6c757d;
                        border: none;
                        border-bottom: 2px solid transparent;
                        padding: 12px 24px;
                        font-weight: 500;
                    }
                    .nav-tabs .nav-link:hover {
                        border-color: transparent;
                        color: #dc3545;
                    }
                    .nav-tabs .nav-link.active {
                        color: #dc3545;
                        background-color: transparent;
                        border-color: transparent transparent #dc3545;
                    }
                    .nav-tabs .nav-link.tab-locked {
                        color: #adb5bd !important;
                        cursor: not-allowed !important;
                        opacity: 0.7;
                    }
                    .nav-tabs .nav-link.tab-locked:hover {
                        color: #adb5bd !important;
                    }
                    .required-field-invalid {
                        border-color: #dc3545 !important;
                        background-color: #fff5f5 !important;
                    }
                    .required-field-invalid:focus {
                        border-color: #dc3545 !important;
                        box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
                    }
                    .custom-desc-textarea:focus {
                        border-color: #dc3545 !important;
                        box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
                    }
                    .work-item-row {
                        cursor: pointer;
                    }
                    .work-item-row:hover {
                        background-color: rgba(0, 0, 0, 0.02) !important;
                    }
                    .work-item-row .form-check-input {
                        cursor: pointer !important;
                    }
                    .work-item-row .form-check-label {
                        cursor: pointer !important;
                    }
                    .work-item-row td {
                        cursor: pointer;
                    }
                    .work-item-checkbox-cell {
                        cursor: pointer !important;
                    }
                `}
            </style>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #dee2e6',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h5 className="mb-1 d-flex align-items-center gap-2">
                        <span className="fw-bold">Generate Estimate</span>
                        {estimateNumber && (
                            <span style={{
                                fontSize: '0.7rem',
                                fontWeight: '500',
                                padding: '3px 10px',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: '6px',
                                color: '#6c757d',
                                letterSpacing: '0.3px'
                            }}>
                                {estimateNumber}
                            </span>
                        )}
                    </h5>
                    <button
                        onClick={onHide}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#6c757d',
                            padding: '0',
                            lineHeight: '1'
                        }}
                    >
                        {!loading && <FontAwesomeIcon icon={faTimes} />}
                    </button>
                </div>

                <Nav variant="tabs" className="px-3" style={{ borderBottom: '1px solid #dee2e6' }}>
                    <Nav.Item>
                        <Nav.Link
                            active={activeTab === 'details'}
                            onClick={() => setActiveTab('details')}
                            style={{ cursor: 'pointer' }}
                        >
                            Generate Estimate
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link
                            active={activeTab === 'work'}
                            onClick={() => {
                                if (!isNextDisabled) setActiveTab('work');
                            }}
                            className={isNextDisabled ? 'tab-locked' : ''}
                            style={{ cursor: isNextDisabled ? 'not-allowed' : 'pointer' }}
                        >
                            Work Required
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <div style={{
                    padding: '20px',
                    overflowY: 'auto',
                    flex: 1
                }}>
                    {activeTab === 'details' && (
                        <>
                            <div className="mb-4">
                                <div className="mb-2 px-2 py-1 bg-light border-start border-danger border-3">
                                    <small className="text-muted fw-bold">PAYEE INFO</small>
                                </div>
                                <Row className="g-2">
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="mb-1">
                                                <small className={isFieldInvalid(payeeDetails.payeeName) ? 'text-danger fw-bold' : 'text-muted'}>
                                                    Payee Name *
                                                </small>
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="Enter payee name"
                                                value={payeeDetails.payeeName}
                                                onChange={(e) =>
                                                    handlePayeeDetailsChange('payeeName', e.target.value)
                                                }
                                                size="sm"
                                                className={`estimate-modal-input ${isFieldInvalid(payeeDetails.payeeName) ? 'required-field-invalid' : ''}`}
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <Form.Label className="mb-1">
                                                <small className={isFieldInvalid(payeeDetails.invoicePostcode) ? 'text-danger fw-bold' : 'text-muted'}>
                                                    Postcode *
                                                </small>
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="Enter postcode"
                                                value={payeeDetails.invoicePostcode}
                                                onChange={(e) =>
                                                    handlePayeeDetailsChange('invoicePostcode', e.target.value)
                                                }
                                                size="sm"
                                                className={`estimate-modal-input ${isFieldInvalid(payeeDetails.invoicePostcode) ? 'required-field-invalid' : ''}`}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="mb-1">
                                                <small className={isFieldInvalid(payeeDetails.invoiceAddress) ? 'text-danger fw-bold' : 'text-muted'}>
                                                    Address Line 1 *
                                                </small>
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="Enter address line 1"
                                                value={payeeDetails.invoiceAddress}
                                                onChange={(e) =>
                                                    handlePayeeDetailsChange('invoiceAddress', e.target.value)
                                                }
                                                size="sm"
                                                className={`estimate-modal-input ${isFieldInvalid(payeeDetails.invoiceAddress) ? 'required-field-invalid' : ''}`}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-2">
                                            <Form.Label className="mb-1">
                                                <small className="text-muted">Address Line 2</small>
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="Enter address line 2"
                                                value={payeeDetails.invoiceAddress2}
                                                onChange={(e) =>
                                                    handlePayeeDetailsChange('invoiceAddress2', e.target.value)
                                                }
                                                size="sm"
                                                className="estimate-modal-input"
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <Form.Label className="mb-1">
                                                <small className="text-muted">Address Line 3</small>
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="Enter address line 3"
                                                value={payeeDetails.invoiceAddress3}
                                                onChange={(e) =>
                                                    handlePayeeDetailsChange('invoiceAddress3', e.target.value)
                                                }
                                                size="sm"
                                                className="estimate-modal-input"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            <div className="mb-4">
                                <div className="mb-2 px-2 py-1 bg-light border-start border-danger border-3">
                                    <small className="text-muted fw-bold">PROPERTY INFO</small>
                                </div>
                                <Row className="g-2">
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="mb-1">
                                                <small className={isFieldInvalid(propertyDetails.propertyAddress) ? 'text-danger fw-bold' : 'text-muted'}>
                                                    Address Line 1 *
                                                </small>
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="Enter property address line 1"
                                                value={propertyDetails.propertyAddress}
                                                onChange={(e) =>
                                                    handlePropertyDetailsChange('propertyAddress', e.target.value)
                                                }
                                                size="sm"
                                                className={`estimate-modal-input ${isFieldInvalid(propertyDetails.propertyAddress) ? 'required-field-invalid' : ''}`}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-2">
                                            <Form.Label className="mb-1">
                                                <small className="text-muted">Address Line 2</small>
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="Enter property address line 2"
                                                value={propertyDetails.propertyAddress2}
                                                onChange={(e) =>
                                                    handlePropertyDetailsChange('propertyAddress2', e.target.value)
                                                }
                                                size="sm"
                                                className="estimate-modal-input"
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <Form.Label className="mb-1">
                                                <small className="text-muted">Address Line 3</small>
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="Enter property address line 3"
                                                value={propertyDetails.propertyAddress3}
                                                onChange={(e) =>
                                                    handlePropertyDetailsChange('propertyAddress3', e.target.value)
                                                }
                                                size="sm"
                                                className="estimate-modal-input"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="mb-1">
                                                <small className={isFieldInvalid(propertyDetails.propertyPostcode) ? 'text-danger fw-bold' : 'text-muted'}>
                                                    Postcode *
                                                </small>
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="Enter postcode"
                                                value={propertyDetails.propertyPostcode}
                                                onChange={(e) => handlePropertyDetailsChange('propertyPostcode', e.target.value)}
                                                size="sm"
                                                className={`estimate-modal-input ${isFieldInvalid(propertyDetails.propertyPostcode) ? 'required-field-invalid' : ''}`}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            <div className="mb-4">
                                <div className="mb-2 px-2 py-1 bg-light border-start border-danger border-3">
                                    <small className="text-muted fw-bold">NOTES</small>
                                </div>
                                <Row className="g-2">
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="mb-1"><small className="text-muted">Notes</small></Form.Label>
                                            <Form.Control
                                                placeholder="Enter notes"
                                                value={propertyDetails.notes}
                                                onChange={(e) => handlePropertyDetailsChange('notes', e.target.value)}
                                                size="sm"
                                                className="estimate-modal-input"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        </>
                    )}

                    {activeTab === 'work' && !isNextDisabled && (
                        <>
                            {showDoorSelector && (
                                <div className="mb-3">
                                    <div
                                        className="door-selector-toggle px-3 py-2 bg-white border rounded d-flex justify-content-between align-items-center"
                                        onClick={() => setIsDoorSelectorOpen(!isDoorSelectorOpen)}
                                        style={{ borderLeft: '3px solid #dc3545' }}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <FontAwesomeIcon icon={faDoorOpen} className="text-danger" />
                                            <span className="fw-bold text-dark">Door Selection</span>
                                            <Badge
                                                bg={selectedDoorIds.size === 0 ? "danger" : selectedDoorIds.size === accessibleDoors.length ? "success" : "warning"}
                                                className="ms-2"
                                            >
                                                {selectedDoorIds.size} / {accessibleDoors.length}
                                            </Badge>
                                        </div>
                                        <FontAwesomeIcon
                                            icon={isDoorSelectorOpen ? faChevronUp : faChevronDown}
                                            className="text-muted"
                                        />
                                    </div>

                                    <Collapse in={isDoorSelectorOpen}>
                                        <div className="mt-2 border rounded bg-light p-3">
                                            <Row className="g-2 mb-2">
                                                <Col>
                                                    <InputGroup size="sm">
                                                        <InputGroup.Text className="bg-white">
                                                            <FontAwesomeIcon icon={faSearch} style={{ fontSize: '11px' }} />
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            placeholder="Search by door number, level, or work..."
                                                            value={doorSearchTerm}
                                                            onChange={(e) => setDoorSearchTerm(e.target.value)}
                                                            size="sm"
                                                            className="estimate-modal-input"
                                                        />
                                                    </InputGroup>
                                                </Col>
                                                <Col xs="auto">
                                                    <Button
                                                        variant="outline-success"
                                                        size="sm"
                                                        onClick={handleSelectAllDoors}
                                                        disabled={selectedDoorIds.size === accessibleDoors.length}
                                                    >
                                                        All
                                                    </Button>
                                                </Col>
                                                <Col xs="auto">
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        onClick={handleDeselectAllDoors}
                                                        disabled={selectedDoorIds.size === 0}
                                                    >
                                                        None
                                                    </Button>
                                                </Col>
                                            </Row>

                                            <div
                                                className="bg-white border rounded p-2"
                                                style={{ maxHeight: '250px', overflowY: 'auto' }}
                                            >
                                                {filteredDoors.length === 0 ? (
                                                    <div className="text-center py-3">
                                                        <small className="text-muted">
                                                            {doorSearchTerm
                                                                ? 'No doors match your search'
                                                                : 'No accessible doors found'}
                                                        </small>
                                                    </div>
                                                ) : (
                                                    <Row className="g-2">
                                                        {[...filteredDoors]
                                                            // .sort((a, b) => {
                                                            //     const numA = parseInt(a.doorNumber, 10) || 0;
                                                            //     const numB = parseInt(b.doorNumber, 10) || 0;
                                                            //     if (numA !== numB) return numA - numB;
                                                            //     return a.doorNumber.localeCompare(b.doorNumber);
                                                            // })
                                                            .map((door) => {
                                                                const doorId = door.id || door.doorNumber;
                                                                const isSelected = selectedDoorIds.has(doorId);
                                                                const workNeeded = door.workNeeded || door.workDone || 'No work specified';
                                                                const workPreview = workNeeded.length > 45
                                                                    ? workNeeded.substring(0, 45) + '...'
                                                                    : workNeeded;

                                                                return (
                                                                    <Col xs={12} sm={6} md={4} lg={3} key={doorId}>
                                                                        <div
                                                                            className={`door-item h-100 ${isSelected ? 'selected' : ''}`}
                                                                            onClick={() => handleToggleDoor(doorId)}
                                                                        >
                                                                            <div className="d-flex align-items-start gap-2">
                                                                                <div className="pt-1">
                                                                                    <FontAwesomeIcon
                                                                                        icon={isSelected ? faCheckSquare : faSquare}
                                                                                        className={isSelected ? 'text-success' : 'text-muted'}
                                                                                        style={{ fontSize: '14px' }}
                                                                                    />
                                                                                </div>
                                                                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                                                    <div className="mb-1">
                                                                                        <div className="fw-bold text-dark text-truncate">
                                                                                            Door {door.doorNumber}
                                                                                        </div>
                                                                                    </div>
                                                                                    <small
                                                                                        className="text-muted d-block"
                                                                                        style={{ fontSize: '0.7rem', lineHeight: '1.3' }}
                                                                                    >
                                                                                        {workPreview}
                                                                                    </small>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </Col>
                                                                );
                                                            })}
                                                    </Row>
                                                )}
                                            </div>
                                        </div>
                                    </Collapse>
                                </div>
                            )}

                            {selectedDoorIds.size === 0 && (
                                <div className="mb-3">
                                    <div className="mb-2 px-2 py-1 bg-light border-start border-danger border-3 d-flex align-items-center justify-content-between">
                                        <small className="text-muted fw-bold">CUSTOM DESCRIPTION</small>
                                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                            One item per line · each line becomes a work item
                                        </small>
                                    </div>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        placeholder={`e.g.\nScrews Missing From Hinge\nReplace Non-Compliant Locks\nIntumescent Strip Installed Incorrectly`}
                                        value={customDescription}
                                        onChange={(e) => {
                                            setCustomDescription(e.target.value);
                                            setLoadedCustomWorkItems([]);
                                        }}
                                        className="custom-desc-textarea"
                                        style={{ fontSize: '0.85rem', resize: 'none' }}
                                    />
                                    {customWorkItems.length > 0 && (
                                        <small className="text-muted mt-1 d-block">
                                            {customWorkItems.length} item{customWorkItems.length !== 1 ? 's' : ''} detected
                                        </small>
                                    )}
                                </div>
                            )}

                            <div className="mb-3">
                                <div className="mb-2 px-2 py-1 bg-light border-start border-danger border-3">
                                    <small className="text-muted fw-bold">WORK ITEMS</small>
                                </div>
                                {activeWorkItems.length === 0 ? (
                                    <div className="text-center py-3 bg-light rounded border">
                                        <small className="text-muted">
                                            {selectedDoorIds.size === 0
                                                ? 'No doors selected. Select doors above or enter a custom description to add work items.'
                                                : 'No work items specified for selected doors'}
                                        </small>
                                    </div>
                                ) : (
                                    <div className="border rounded" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                        <Table size="sm" className="mb-0" hover>
                                            <thead className="table-light sticky-top">
                                                <tr>
                                                    <th style={{ width: '40px' }} className="text-center"></th>
                                                    <th style={{ width: '80px' }} className="text-center">Code</th>
                                                    {selectedDoorIds.size === 0 && (
                                                        <th style={{ width: '90px' }} className="text-center">Door</th>
                                                    )}
                                                    <th className="text-center">Description</th>
                                                    <th style={{ width: '80px' }} className="text-center">Qty</th>
                                                    <th style={{ width: '140px' }} className="text-end">Price</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {activeWorkItems.map((item, index) => {
                                                    const itemKey = item.toLowerCase();
                                                    const itemData = workItemsMap[itemKey];
                                                    const isSelected = selectedWorkItems.has(item);

                                                    return (
                                                        <tr
                                                            key={index}
                                                            className={`work-item-row ${isSelected ? 'table-primary' : ''}`}
                                                        >
                                                            <td
                                                                className="align-middle text-center work-item-checkbox-cell"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedWorkItems(prev => {
                                                                        const next = new Set(prev);
                                                                        next.has(item) ? next.delete(item) : next.add(item);
                                                                        return next;
                                                                    });
                                                                }}
                                                            >
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={() => { }}
                                                                    style={{ cursor: 'pointer', paddingBottom: '6px' }}
                                                                    className="d-flex justify-content-center align-items-center m-0"
                                                                />
                                                            </td>
                                                            <td className="align-middle">
                                                                <Form.Control
                                                                    type="text"
                                                                    size="sm"
                                                                    value={codes[item] || ""}
                                                                    onChange={(e) =>
                                                                        setCodes(prev => ({
                                                                            ...prev,
                                                                            [item]: e.target.value
                                                                        }))
                                                                    }
                                                                    className="text-center estimate-modal-input"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                            </td>
                                                            {selectedDoorIds.size === 0 && (
                                                                <td className="align-middle">
                                                                    <Form.Control
                                                                        type="text"
                                                                        size="sm"
                                                                        value={doorRefs[item] || ""}
                                                                        placeholder="C2 / R2"
                                                                        onChange={(e) =>
                                                                            setDoorRefs(prev => ({
                                                                                ...prev,
                                                                                [item]: e.target.value
                                                                            }))
                                                                        }
                                                                        className="text-center estimate-modal-input"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </td>
                                                            )}
                                                            <td className="align-middle text-center">
                                                                <small>{workItemDescriptions[item] || item}</small>
                                                            </td>
                                                            <td className="align-middle">
                                                                <Form.Control
                                                                    type="number"
                                                                    size="sm"
                                                                    min="1"
                                                                    step="1"
                                                                    value={quantities[item] || ""}
                                                                    onChange={(e) =>
                                                                        setQuantities((prev) => ({
                                                                            ...prev,
                                                                            [item]: e.target.value,
                                                                        }))
                                                                    }
                                                                    className="text-center estimate-modal-input"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                            </td>
                                                            <td className="align-middle">
                                                                <InputGroup size="sm" className="estimate-modal-input-group">
                                                                    <InputGroup.Text className="bg-white">
                                                                        <FontAwesomeIcon icon={faPoundSign} style={{ fontSize: '10px' }} />
                                                                    </InputGroup.Text>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="0"
                                                                        step="0.01"
                                                                        value={prices[item] || ""}
                                                                        onChange={(e) =>
                                                                            setPrices((prev) => ({
                                                                                ...prev,
                                                                                [item]: e.target.value,
                                                                            }))
                                                                        }
                                                                        className="text-end estimate-modal-input"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </InputGroup>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>

                            <div>
                                <div className="mb-2 px-2 py-1 bg-light border-start border-danger border-3">
                                    <small className="text-muted fw-bold">COSTS & SUMMARY</small>
                                </div>
                                <Row className="g-2">


                                    <Col md={5}>
                                        <div className="mb-2">
                                            <small className="fw-bold">Discount</small>
                                        </div>

                                        <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
                                            {[
                                                ['', 'None'],
                                                ['10', '10%'],
                                                ['15', '15%'],
                                                ['20', '20%'],
                                                ['25', '25%'],
                                            ].map(([value, label]) => (
                                                <label
                                                    key={label}
                                                    className="d-flex align-items-center gap-1 mb-0"
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="discountGroup"
                                                        checked={
                                                            value === ''
                                                                ? !Number(discountValue)
                                                                : Number(discountValue) === Number(value)
                                                        }
                                                        onChange={() => setDiscountValue(value)}
                                                        style={{
                                                            width: '15px',
                                                            height: '15px',
                                                            accentColor: 'red',
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                    <small>{label}</small>
                                                </label>
                                            ))}

                                            {(totals.discount > 0 || isEditingDiscount) && (
                                                <div
                                                    className="input-group input-group-sm"
                                                    style={{ width: '100px' }}
                                                >
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        placeholder="0"
                                                        min="0"
                                                        max="100"
                                                        step="0.01"
                                                        value={discountValue}
                                                        onFocus={(e) => {
                                                            e.currentTarget.style.borderColor = '#b11111';
                                                            e.currentTarget.style.boxShadow =
                                                                '0 0 0 0.2rem rgba(177, 17, 17, 0.2)';
                                                        }}
                                                        onBlur={(e) => {
                                                            e.currentTarget.style.borderColor = '';
                                                            e.currentTarget.style.boxShadow = '';
                                                        }}
                                                        onChange={(e) => {
                                                            const value = e.target.value;

                                                            if (
                                                                value === '' ||
                                                                (
                                                                    /^\d*(\.\d{0,2})?$/.test(value) &&
                                                                    Number(value) >= 0 &&
                                                                    Number(value) <= 100
                                                                )
                                                            ) {
                                                                setDiscountValue(value);
                                                            }
                                                        }}
                                                    />
                                                    <span className="input-group-text">%</span>
                                                </div>
                                            )}
                                        </div>

                                        <small className="fw-semibold d-block mb-2">Charges</small>
                                        <div className="d-flex flex-column gap-1">
                                            {[
                                                [includeVAT, setIncludeVAT, 'vat', '20% VAT'],
                                                [includeManagementFee, setIncludeManagementFee, 'mgmt', '15% Management Fee'],
                                            ].map(([checked, setter, id, label]) => (
                                                <div
                                                    key={id}
                                                    className="d-flex align-items-center gap-2"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setter(!checked)}
                                                >
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`charge-${id}`}
                                                        checked={checked}
                                                        onChange={() => setter(!checked)}
                                                        style={{ cursor: 'pointer' }}
                                                        className="m-0 p-1"
                                                    />
                                                    <small style={{ cursor: 'pointer', lineHeight: 1, marginTop: '8px' }}>
                                                        {label}
                                                    </small>
                                                </div>
                                            ))}
                                        </div>
                                    </Col>

                                    <Col md={7}>
                                        <Table size="sm" bordered className="mb-0 align-middle">
                                            <tbody>
                                                <tr>
                                                    <td className="bg-light"><small>Items Total</small></td>
                                                    <td className="text-end"><small className="fw-semibold">£{totals.itemsTotal.toFixed(2)}</small></td>
                                                </tr>
                                                <tr>
                                                    <td className="bg-light"><small>Labor Cost</small></td>
                                                    <td className="text-end"><small className="fw-semibold">£{totals.labor.toFixed(2)}</small></td>
                                                </tr>

                                                {(totals.discount > 0 || isEditingDiscount) && (
                                                    <tr>
                                                        <td className="bg-light">
                                                            <small>
                                                                Discount ({Number(totals.discountPercentage.toFixed(2))}%)
                                                            </small>
                                                        </td>
                                                        <td className="text-end">
                                                            <small
                                                                className="fw-semibold text-danger"
                                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                                            >
                                                                −£
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm text-end"
                                                                    aria-label="Discount amount in pounds"
                                                                    min="0"
                                                                    max={totals.itemsTotal + totals.labor}
                                                                    step="0.01"
                                                                    value={discountAmountInput}
                                                                    onFocus={(e) => {
                                                                        setIsEditingDiscount(true);
                                                                        e.currentTarget.style.borderColor = '#b11111';
                                                                        e.currentTarget.style.boxShadow =
                                                                            '0 0 0 2px rgba(177, 17, 17, 0.1)';
                                                                    }}
                                                                    onChange={(e) => {
                                                                        const value = e.currentTarget.value;
                                                                        if (!/^\d*(\.\d{0,2})?$/.test(value)) return;

                                                                        const base = Math.max(0, totals.itemsTotal + totals.labor);
                                                                        const enteredAmount = Math.max(0, Number(value) || 0);
                                                                        const amount = Math.min(base, enteredAmount);

                                                                        setDiscountAmountInput(
                                                                            enteredAmount > base ? amount.toFixed(2) : value
                                                                        );
                                                                        setDiscountValue(
                                                                            base > 0 ? ((amount / base) * 100).toFixed(2) : '0'
                                                                        );
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        e.currentTarget.style.borderColor = '';
                                                                        e.currentTarget.style.boxShadow = '';
                                                                        setIsEditingDiscount(false);
                                                                    }}
                                                                    style={{
                                                                        width: '90px',
                                                                        height: '24px',
                                                                        minHeight: 0,
                                                                        padding: '1px 5px',
                                                                        fontSize: 'inherit',
                                                                        fontWeight: 600,
                                                                        color: '#dc3545',
                                                                        borderRadius: '4px',
                                                                    }}
                                                                />
                                                            </small>
                                                        </td>
                                                    </tr>
                                                )}

                                                <tr className="table-active">
                                                    <td><small className="fw-bold">Subtotal</small></td>
                                                    <td className="text-end"><small className="fw-bold">£{totals.subtotal.toFixed(2)}</small></td>
                                                </tr>
                                                {includeManagementFee && (
                                                    <tr>
                                                        <td className="bg-light"><small>Management Fee (15%)</small></td>
                                                        <td className="text-end"><small className="fw-semibold">£{totals.managementFee.toFixed(2)}</small></td>
                                                    </tr>
                                                )}
                                                {includeVAT && (
                                                    <tr>
                                                        <td className="bg-light"><small>VAT (20%)</small></td>
                                                        <td className="text-end"><small className="fw-semibold">£{totals.vat.toFixed(2)}</small></td>
                                                    </tr>
                                                )}
                                                <tr className="table-success">
                                                    <td><small className="fw-bold">TOTAL</small></td>
                                                    <td className="text-end"><small className="fw-bold">£{totals.total.toFixed(2)}</small></td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Col>
                                </Row>
                            </div>

                            <div style={{ paddingTop: '20px' }}>
                                <div className="d-flex gap-2">
                                    <div
                                        className="alert alert-info flex-fill"
                                        style={{ marginBottom: '0', fontSize: '0.85rem', padding: '8px 12px' }}
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px', fontSize: '14px' }} />
                                        <strong>Tip:</strong> Set prices at building level first, then customize per door if needed.
                                    </div>
                                    <div
                                        className="alert alert-secondary flex-fill d-flex align-items-center gap-2"
                                        style={{ marginBottom: '0', fontSize: '0.85rem', padding: '8px 12px', cursor: 'pointer' }}
                                        onClick={() => setExportNewLayout(!exportNewLayout)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={exportNewLayout}
                                            onChange={() => setExportNewLayout(!exportNewLayout)}
                                            style={{ marginBottom: '6px', cursor: 'pointer', width: '15px', height: '15px', accentColor: '#6c757d' }}
                                        />
                                        <span><strong>Export</strong> with new layout</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div style={{
                    padding: '12px 20px',
                    borderTop: '1px solid #dee2e6',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '8px'
                }}>
                    <Button variant="outline-secondary" onClick={onHide} disabled={loading} size="sm">
                        Cancel
                    </Button>
                    <div className="d-flex gap-2">
                        {activeTab === 'details' && (
                            <Button
                                variant="primary"
                                onClick={() => setActiveTab('work')}
                                size="sm"
                                disabled={isNextDisabled}
                            >
                                Next
                            </Button>
                        )}
                        {activeTab === 'work' && (
                            <>
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => setActiveTab('details')}
                                    disabled={loading}
                                    size="sm"
                                >
                                    Back
                                </Button>

                                <Button
                                    variant="success"
                                    onClick={handleGenerateEstimate}
                                    disabled={
                                        isNextDisabled ||
                                        loading ||
                                        (selectedDoorIds.size === 0 && customWorkItems.length === 0) ||
                                        !activeWorkItems.some(item => parseFloat(prices[item]) > 0 && parseFloat(quantities[item]) > 0)
                                    }
                                    size="sm"
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Generating...
                                        </>
                                    ) : (
                                        'Generate PDF'
                                    )}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default EstimateModal;